// Borlabs -> Shopify consent reconciliation.
// Borlabs (WordPress parent domain) is the master consent source. On every Shopify
// page load the Borlabs cookie is read, mapped to the four Shopify Customer Privacy
// categories and written to Shopify if it differs from the CURRENT Shopify consent
// state. This corrects a stale Shopify state left behind e.g. by an earlier checkout.
//
// Debug logging is gated behind `window.borlabsConsentDebug` (set it to a truthy value
// to see the verbose trace). Errors are always logged.

const DEBUG = () => !!window.borlabsConsentDebug;
const log = (...args) => {
	if (DEBUG()) console.log('[consent]', ...args);
};
const warn = (...args) => {
	if (DEBUG()) console.warn('[consent]', ...args);
};

log('Consent handler loaded');

const MAX_WAIT_TIME = 10000; // 10 seconds
const MAX_RETRIES = 2; // Maximum number of retries
const RETRY_INTERVAL = 200; // 200 milliseconds

const pollUntil = (conditionFunction, onSuccess, onFailure, timeout = MAX_WAIT_TIME) => {
	const startTime = Date.now();
	const interval = setInterval(() => {
		log('Polling for condition...');
		if (conditionFunction()) {
			clearInterval(interval);
			onSuccess();
		} else if (Date.now() - startTime > timeout) {
			clearInterval(interval);
			console.error('[consent] Timeout reached while waiting for condition');
			if (onFailure) {
				onFailure();
			}
		}
	}, RETRY_INTERVAL);
};
const publishEventToShopifyFromParam = (param) => {
	const eventName = 'post:paramsDataId';
	const eventData = {
		event: eventName,
		data: param,
	};
	pollUntil(
		() => window.Shopify && window.Shopify.analytics,
		() => {
			window.Shopify.analytics.publish(eventName, eventData);
			log('Shopify object is available, event published:', eventName, eventData);
		},
		() => {
			console.error('[consent] Failed to publish event to Shopify after retries');
		},
	);
};
const getCookie = (key) => {
	const decodedCookie = decodeURIComponent(document.cookie);
	const ca = decodedCookie.split(';');
	for (let i = 0; i < ca.length; i++) {
		let c = ca[i];
		const cKey = c.split('=')[0].trim();

		if (cKey === key) {
			const value = c.split('=')[1].trim();
			log(`Cookie found -> ${cKey}`);
			return value;
		}
	}
	return '';
};
const getBorlabsCookie = () => {
	const cookieValue = getCookie('borlabs-cookie');
	if (!cookieValue) {
		log('No borlabs-cookie found');
		return null;
	}
	try {
		const decoded = decodeURIComponent(cookieValue);
		const parsed = JSON.parse(decoded);
		log('Borlabs cookie parsed:', parsed);
		return parsed;
	} catch (error) {
		console.error('[consent] Error parsing borlabs-cookie:', error);
		return null;
	}
};
const mapBorlabsToShopifyConsent = (borlabsData) => {
	if (!borlabsData || !borlabsData.consents) {
		return null;
	}
	const statisticsConsents = borlabsData.consents.statistics;
	const marketingConsents = borlabsData.consents.marketing;
	const preferencesConsents = borlabsData.consents.preferences;
	const requiredVendors = window.borlabsMarketingVendors || ['meta', 'google'];
	// "all required" rule for preferences as well: every configured preferences vendor
	// must be present in the Borlabs preferences group. Vendor list is configurable via
	// window.borlabsPreferencesVendors (theme setting). See docs/consent-mapping-P26144.md.
	const requiredPreferencesVendors = window.borlabsPreferencesVendors || [];
	return {
		analytics: Array.isArray(statisticsConsents) && statisticsConsents.includes('mapp'),
		marketing:
			Array.isArray(marketingConsents) && requiredVendors.every((vendor) => marketingConsents.includes(vendor)),
		// preferences stays false while no preferences vendors are configured (no vacuous true).
		preferences:
			requiredPreferencesVendors.length > 0 &&
			Array.isArray(preferencesConsents) &&
			requiredPreferencesVendors.every((vendor) => preferencesConsents.includes(vendor)),
		sale_of_data: false,
	};
};
const setShopifyConsent = (consentData, source) => {
	pollUntil(
		() => window.Shopify && typeof window.Shopify.customerPrivacy === 'object',
		() => {
			window.Shopify.customerPrivacy.setTrackingConsent(consentData, () => {
				log(`Consent set via ${source}`);
			});
			setTimeout(() => {
				if (!sessionStorage.getItem('consentReloadedOnce')) {
					warn('Fallback: consent event not received, forcing reload');
					sessionStorage.setItem('consentReloadedOnce', '1');
					window.location.reload();
				}
			}, 4000); // 4 seconds fallback
		},
		() => {
			console.error('[consent] Failed to set consent data after retries, customer privacy not found');
		},
	);
};
// Coerce Shopify Customer Privacy values (which may be 'yes' | 'no' | '' | boolean) to a boolean.
const toBool = (value) => value === true || value === 'yes';
// Read the CURRENT Shopify consent as a normalized boolean object, or null if unavailable.
const getCurrentShopifyConsent = () => {
	try {
		const current = window.Shopify.customerPrivacy.currentVisitorConsent();
		return {
			analytics: toBool(current.analytics),
			marketing: toBool(current.marketing),
			preferences: toBool(current.preferences),
			sale_of_data: toBool(current.sale_of_data),
		};
	} catch (error) {
		warn('Could not read current Shopify consent:', error);
		return null;
	}
};
const consentMatches = (a, b) =>
	!!a &&
	!!b &&
	a.analytics === b.analytics &&
	a.marketing === b.marketing &&
	a.preferences === b.preferences &&
	a.sale_of_data === b.sale_of_data;
const setShopifyConsentFromBorlabs = () => {
	const borlabsData = getBorlabsCookie();
	if (!borlabsData) {
		log('No borlabs cookie data available');
		return false;
	}
	const consentData = mapBorlabsToShopifyConsent(borlabsData);
	if (!consentData) {
		log('Could not map borlabs consent data');
		return false;
	}
	log('Mapped borlabs consent:', consentData);
	// Borlabs is the master: compare the mapped Borlabs consent against the CURRENT Shopify
	// consent state (NOT against the last Borlabs sync). This way a stale Shopify state — e.g.
	// changed during an earlier checkout while the Borlabs cookie stayed the same — is detected
	// and corrected on the next page load.
	const currentShopify = getCurrentShopifyConsent();
	if (consentMatches(consentData, currentShopify)) {
		log('Shopify consent already matches Borlabs, skipping write');
		document.dispatchEvent(new CustomEvent('consent:already-synced', { bubbles: true }));
		return false;
	}
	log('Shopify consent differs from Borlabs, overwriting (Borlabs = master)', {
		currentShopify,
		borlabs: consentData,
	});
	setShopifyConsent(consentData, 'borlabs cookie');
};
document.addEventListener('consent:params', function (event) {
	log('Consent param event received:', event.detail);

	const urlParams = event.detail.params;
	// marketing technology params
	const paramIDs = ['gbraid', 'wbraid', 'gclid', 'fbclid', 'utm_source', 'everID'];
	const idParamFound = paramIDs.find((param) => urlParams.has(param));
	if (idParamFound) {
		const param = {
			name: idParamFound,
			value: urlParams.get(idParamFound),
		};
		publishEventToShopifyFromParam(param);
	} else {
		log('No marketing param found');
	}
	// Read consent from borlabs cookie
	pollUntil(
		() => window.Shopify && typeof window.Shopify.loadFeatures === 'function',
		() => {
			log('Shopify consent API is available');
			window.Shopify.loadFeatures(
				[
					{
						name: 'consent-tracking-api',
						version: '0.1',
					},
				],
				(err) => {
					if (err) {
						console.error('[consent] Error loading Shopify features:', err);
						return;
					}
					log('Shopify consent-tracking feature loaded');
					setShopifyConsentFromBorlabs();
				},
			);
		},
		() => {
			console.error('[consent] Failed to load Shopify consent API after retries');
		},
	);
});

document.addEventListener('visitorConsentCollected', (event) => {
	log('Consent set', event.detail);
	const consentData = {
		analyticsAllowed: event.detail?.analyticsAllowed || false,
		marketingAllowed: event.detail?.marketingAllowed || false,
		preferencesAllowed: event.detail?.preferencesAllowed || false,
	};
	if (!window._saleOfDataOverrideApplied) {
		// Re-set consent with sale_of_data explicitly false (Shopify banner doesn't include it)
		window._saleOfDataOverrideApplied = true;
		setShopifyConsent(
			{
				analytics: consentData.analyticsAllowed,
				marketing: consentData.marketingAllowed,
				preferences: consentData.preferencesAllowed,
				sale_of_data: false,
			},
			'shopify banner (sale_of_data override)',
		);
		return;
	} else {
		window._saleOfDataOverrideApplied = false;
	}
	// reload the page to apply the consent
	if (sessionStorage.getItem('consentReloadedOnce') === '1') return;
	sessionStorage.setItem('consentReloadedOnce', '1');
	window.location.reload();
});
